import {
  ColumnDetail,
  ComplexSelectionFormValue,
  RowDetail,
} from '../../../../../../../ui/gzSelectableTableRowGroup/types';
import { GeneralDisplaySilentModeShowPopupsSettings } from '@nirvana/shared/dtos/types/policies/common';
import { translateFn } from '../../../../../../../utils/translator.helper';
import {
  isApplicationControlVisibleForGeneralNotificationsShowPopupsSettings,
  isEncryptionVisibleForGeneralNotificationsShowPopupsSettings,
  isExchangeVisibleForGeneralNotificationsShowPopupsSettings,
  isSandboxVisibleForGeneralNotificationsShowPopupsSettings,
} from '../../../../utils/uiSettings/general/general.values';

export enum GeneralNotificationsDisplayPopupsKey {
  GENERAL = 'general',
  UPDATE = 'update',
  ANTIMLAWARE = 'antimalware',
  SANDBOX = 'sandboxAnalyzer',
  FIREWALL = 'firewall',
  APPLICATION_CONTROL = 'applicationControl',
  CONTENT_CONTROL = 'contentControl',
  DEVICE_CONTROL = 'deviceControl',
  EXCHANGE = 'exchange',
  ENCRYPTION = 'encryption',
}

export enum GeneralNotificationsDisplayPopupsColumn {
  INFO = 1,
  LOW = 2,
  CRITICAL = 3,
}

export const columns = (): ColumnDetail<GeneralNotificationsDisplayPopupsColumn>[] => {
  return [
    {
      column: GeneralNotificationsDisplayPopupsColumn.INFO,
      label: translateFn('Info'),
    },
    {
      column: GeneralNotificationsDisplayPopupsColumn.LOW,
      label: translateFn('Low'),
    },
    {
      column: GeneralNotificationsDisplayPopupsColumn.CRITICAL,
      label: translateFn('Critical'),
    },
  ];
};

export const rows = (): RowDetail<GeneralNotificationsDisplayPopupsKey, GeneralNotificationsDisplayPopupsColumn>[] => [
  { key: GeneralNotificationsDisplayPopupsKey.GENERAL, label: translateFn('General') },
  { key: GeneralNotificationsDisplayPopupsKey.UPDATE, label: translateFn('Update') },
  { key: GeneralNotificationsDisplayPopupsKey.ANTIMLAWARE, label: translateFn('Antimalware') },
  {
    key: GeneralNotificationsDisplayPopupsKey.SANDBOX,
    label: translateFn('Sandbox Analyzer'),
    hidden: !isSandboxVisibleForGeneralNotificationsShowPopupsSettings(),
  },
  { key: GeneralNotificationsDisplayPopupsKey.FIREWALL, label: translateFn('Firewall') },
  {
    key: GeneralNotificationsDisplayPopupsKey.APPLICATION_CONTROL,
    label: translateFn('Application Control'),
    hidden: !isApplicationControlVisibleForGeneralNotificationsShowPopupsSettings(),
  },
  {
    key: GeneralNotificationsDisplayPopupsKey.CONTENT_CONTROL,
    label: translateFn('Content Control'),
    availableColumns: [GeneralNotificationsDisplayPopupsColumn.INFO, GeneralNotificationsDisplayPopupsColumn.LOW],
  },
  {
    key: GeneralNotificationsDisplayPopupsKey.DEVICE_CONTROL,
    label: translateFn('Device Control'),
    availableColumns: [GeneralNotificationsDisplayPopupsColumn.INFO, GeneralNotificationsDisplayPopupsColumn.LOW],
  },
  {
    key: GeneralNotificationsDisplayPopupsKey.EXCHANGE,
    label: translateFn('Exchange Protection'),
    hidden: !isExchangeVisibleForGeneralNotificationsShowPopupsSettings(),
  },
  {
    key: GeneralNotificationsDisplayPopupsKey.ENCRYPTION,
    label: translateFn('Encryption'),
    hidden: !isEncryptionVisibleForGeneralNotificationsShowPopupsSettings(),
  },
];

export function convertDbValueToFormValue(
  categories: GeneralDisplaySilentModeShowPopupsSettings
): ComplexSelectionFormValue<GeneralNotificationsDisplayPopupsKey, GeneralNotificationsDisplayPopupsColumn> {
  return Object.fromEntries(
    Object.entries(categories).map(([key, value]) => [
      key as GeneralNotificationsDisplayPopupsKey,
      {
        enabled: true,
        column: [
          ...(value.info ? [GeneralNotificationsDisplayPopupsColumn.INFO] : []),
          ...(value.low ? [GeneralNotificationsDisplayPopupsColumn.LOW] : []),
          ...(value.critical ? [GeneralNotificationsDisplayPopupsColumn.CRITICAL] : []),
        ],
      },
    ])
  );
}

export function convertFormValueToDbValue(
  categories: ComplexSelectionFormValue<GeneralNotificationsDisplayPopupsKey, GeneralNotificationsDisplayPopupsColumn>
): GeneralDisplaySilentModeShowPopupsSettings {
  const dbValue = {};

  Object.entries(categories).forEach(([key, value]) => {
    if (
      key === GeneralNotificationsDisplayPopupsKey.SANDBOX &&
      !isSandboxVisibleForGeneralNotificationsShowPopupsSettings()
    ) {
      return;
    }

    if (
      key === GeneralNotificationsDisplayPopupsKey.APPLICATION_CONTROL &&
      !isApplicationControlVisibleForGeneralNotificationsShowPopupsSettings()
    ) {
      return;
    }

    if (
      key === GeneralNotificationsDisplayPopupsKey.EXCHANGE &&
      !isExchangeVisibleForGeneralNotificationsShowPopupsSettings()
    ) {
      return;
    }

    if (
      key === GeneralNotificationsDisplayPopupsKey.ENCRYPTION &&
      !isEncryptionVisibleForGeneralNotificationsShowPopupsSettings()
    ) {
      return;
    }

    dbValue[key] = {
      info: value.column.includes(GeneralNotificationsDisplayPopupsColumn.INFO),
      low: value.column.includes(GeneralNotificationsDisplayPopupsColumn.LOW),
      critical: value.column.includes(GeneralNotificationsDisplayPopupsColumn.CRITICAL),
    };
  });

  return dbValue as GeneralDisplaySilentModeShowPopupsSettings;
}
